import * as React from 'react'
import { FC, useCallback, useEffect } from 'react'
import { Formik, FormikErrors } from 'formik'

import { initialValues, TenantLoginInput } from './typings'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'

import { isValidEmail, updateLoadingState } from '@sales-center/common'
import { LoginFormFields } from './LoginFormFields'
import { useNavigate, useLocation } from 'react-router-dom'
import { Logo } from '../TopToolbar/Logo'
import { getStyles } from './useStyles'
import { Footer } from './Footer'
import { loginUser } from '@user/store/authorization/api'
import { toast } from '@sales-center/components'
import {
  clearRedirectUrl,
  selectCurrentUser,
  selectRedirectUrl,
  selectUserAuthenticated,
} from '@user/store/authorization/userSlice'
import { useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import { guestRoleName } from '@user/components/Settings/Users/UpdateUser/constants'

export const LoginForm: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()

  const authenticated = useSelector(selectUserAuthenticated)
  const redirectUrl = useSelector(selectRedirectUrl)
  const currentUser = useSelector(selectCurrentUser)

  const onSubmit = useCallback(
    async (formModel: TenantLoginInput) => {
      dispatch(updateLoadingState('visible'))
      const response = await dispatch(loginUser(formModel))

      if (loginUser.fulfilled.match(response)) {
        const isGuestUser = response.payload?.roleName === guestRoleName
        if (isGuestUser) {
          navigate('/scdashboard')
        } else {
          if (!!redirectUrl) {
            navigate('/scdashboard', { replace: true })
            navigate(redirectUrl)
            dispatch(clearRedirectUrl())
          } else if (location.pathname != '/scdashboard')
            navigate('/scdashboard')
        }
      } else {
        toast.error(
          `${
            response.payload
              ? response.payload
              : (response.payload as any).Message
          }`,
        )
      }
      dispatch(updateLoadingState('hidden'))
    },
    [dispatch, navigate, location.pathname, redirectUrl],
  )

  const validate = useCallback((model: TenantLoginInput) => {
    const errors: FormikErrors<TenantLoginInput> = {}
    const { email, password } = model
    if (email.trim() == '') errors.email = 'Required'
    else if (!isValidEmail(email)) errors.email = 'Please enter valid mail'

    if (password.trim() == '') errors.password = 'Required'

    return errors
  }, [])

  useEffect(() => {
    if (authenticated && location.pathname !== '/scdashboard') {
      navigate('/scdashboard')
    }
  }, [authenticated, currentUser?.roleName, navigate, location.pathname])

  const styles = getStyles(useTheme())
  return authenticated ? null : (
    <>
      <Box sx={styles.logoContainer}>
        <Logo
          colorful={true}
          style={{ width: '361px', height: '252px' }}
        ></Logo>
      </Box>
      <Formik
        initialValues={initialValues}
        validate={validate}
        onSubmit={onSubmit}
      >
        <LoginFormFields />
      </Formik>
      <Footer></Footer>
    </>
  )
}
LoginForm.displayName = 'LoginForm'
